import { httpClient, BASE_URL } from "@/shared/lib/httpClient";
import { refreshAccessToken } from "@/features/auth/utils/tokenRefresh";
import { getCurrentToken } from "@/features/auth";
import i18n from "@/shared/lib/i18n";
import {
    Project,
    ProjectCreateRequest,
    ProjectListResponse,
    ProjectUpdateRequest,
    AddMemberRequest,
    AddMemberResponse,
    SessionMember,
    Agent
} from "@/shared/types/session";

export type ExternalSyncEvent =
    | { type: "progress"; table: string; message: string }
    | { type: "removed"; table: string }
    | {
        type: "complete";
        added: string[];
        removed: string[];
        unchanged_count: number;
        external_synced_at: string | null;
    }
    | { type: "error"; message: string };

export type ExternalSyncCompleteEvent = Extract<ExternalSyncEvent, { type: "complete" }>;

export class SyncAlreadyRunningError extends Error {
    constructor(message = "Sync already in progress") {
        super(message);
        this.name = "SyncAlreadyRunningError";
    }
}

// Agentes (geralmente usados na criação de projetos)
export async function getAgents(): Promise<Agent[]> {
    return httpClient.get<Agent[]>('/agents');
}

// Projetos
export async function createProject(data: ProjectCreateRequest): Promise<Project> {
    return httpClient.post<Project>('/projects', data);
}

export async function getUserProjects(): Promise<ProjectListResponse> {
    return httpClient.get<ProjectListResponse>('/projects');
}

export async function getProject(projectId: string): Promise<Project> {
    return httpClient.get<Project>(`/projects/${projectId}`);
}

export async function updateProject(projectId: string, data: ProjectUpdateRequest): Promise<Project> {
    return httpClient.put<Project>(`/projects/${projectId}`, data);
}

export async function deleteProject(projectId: string): Promise<{ success: boolean; message: string }> {
    return httpClient.delete<{ success: boolean; message: string }>(`/projects/${projectId}`);
}

// Membros do Projeto
export async function getProjectMembers(projectId: string): Promise<SessionMember[]> {
    const response = await httpClient.get<SessionMember[] | { members?: SessionMember[] }>(`/projects/${projectId}/members`);
    if (Array.isArray(response)) return response;
    return response.members || [];
}

export async function addProjectMember(projectId: string, memberData: AddMemberRequest): Promise<AddMemberResponse> {
    return httpClient.post<AddMemberResponse>(`/projects/${projectId}/members`, memberData);
}

export async function removeProjectMember(projectId: string, memberId: number): Promise<{ success: boolean; message: string }> {
    return httpClient.delete<{ success: boolean; message: string }>(`/projects/${projectId}/members/${memberId}`);
}

export async function updateMemberRole(projectId: string, memberId: number, role: 'owner' | 'member'): Promise<{ success: boolean; message: string }> {
    return httpClient.patch<{ success: boolean; message: string }>(`/projects/${projectId}/members/${memberId}`, { role });
}

export async function leaveProject(projectId: string): Promise<{ success: boolean; message: string }> {
    return httpClient.post<{ success: boolean; message: string }>(`/projects/${projectId}/leave`);
}

export async function syncProjectExternalTables(
    projectId: string,
    onEvent?: (event: ExternalSyncEvent) => void,
    signal?: AbortSignal,
): Promise<ExternalSyncCompleteEvent | null> {
    const freshToken = await refreshAccessToken();
    const token = freshToken || getCurrentToken();

    const response = await fetch(`${BASE_URL}/projects/${projectId}/external/sync`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
            "Accept-Language": i18n.language || "pt-BR",
        },
        signal,
    });

    if (!response.ok) {
        if (response.status === 409) {
            const body = await response.json().catch(() => ({})) as { detail?: string };
            throw new SyncAlreadyRunningError(body.detail ?? "Sync already in progress");
        }
        const body = await response.json().catch(() => ({})) as { detail?: string };
        throw new Error(body.detail ?? `Sync failed (${response.status})`);
    }

    if (!response.body) {
        throw new Error("Sync stream not available");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let completeEvent: ExternalSyncCompleteEvent | null = null;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
            if (!line.startsWith("data: ")) continue;

            const event = JSON.parse(line.slice(6)) as ExternalSyncEvent;
            onEvent?.(event);

            if (event.type === "complete") {
                completeEvent = event;
            } else if (event.type === "error") {
                throw new Error(event.message);
            }
        }
    }

    return completeEvent;
}

const ProjectService = {
    getAgents,
    createProject,
    getUserProjects,
    getProject,
    updateProject,
    deleteProject,
    getProjectMembers,
    addProjectMember,
    removeProjectMember,
    updateMemberRole,
    leaveProject,
    syncProjectExternalTables,
};

export default ProjectService;
